// Zustand store for the agent chat surface — the add-in's chat-thread
// state (messages + streaming status). Holds UI state only; the BE owns
// every reasoning step (W1). The reducer just buffers token chunks into
// the current streaming message.
//
// W5 (single-writer): user-scoped → exposes reset(), wired into
// useAuth.logout() alongside the other stores. A new store doubles the
// cross-user-leak surface; the reset wiring + the relogin-empty test are
// the load-bearing guards. Documented per the useCaseStore example.
//
// Selector discipline: components subscribe to narrow slices
// (`useAgentStore(s => s.isStreaming)`), never the whole object — the
// `messages` array is rebuilt on every chunk patch.

import { create } from 'zustand'
import type { ChatMessage, AgentStatus } from '@/domain/agent/entity'

interface AgentState {
  messages: ChatMessage[]
  /** True while a turn is mid-stream (composer disabled, spinner shown). */
  isStreaming: boolean
  /** Presentation label for the current pipeline node (W1 — looked up by
   *  the hook, never a computed verdict). Empty when idle. */
  statusText: string
  /** BE conversation pointer for multi-turn threading. Null = fresh. */
  conversationId: string | null
  /** Append a fully-formed message (user turn, or a seeded agent turn). */
  addMessage: (message: ChatMessage) => void
  /** Buffer a token delta into the CURRENT streaming agent message. No-op
   *  if the last message is not a streaming agent turn. */
  patchStreaming: (delta: string) => void
  /** Replace (not append) the CURRENT streaming agent message's text. Used to
   *  swap an optimistically-streamed answer prefix for the firewall-block copy
   *  when the BE withholds the full answer. No-op if not a streaming turn. */
  replaceStreamingText: (text: string) => void
  /** Finalise the current streaming message into a terminal status. */
  finalize: (status: ChatMessage['status']) => void
  /** Set the presentation status label (from a `status` event). */
  setStatus: (statusText: AgentStatus) => void
  /** Toggle the streaming flag. */
  setStreaming: (isStreaming: boolean) => void
  /** Anchor the BE conversation id (from a multi-turn thread). */
  setConversationId: (conversationId: string | null) => void
  /** W5 — clear all user-scoped state. Called on logout. */
  reset: () => void
}

const EMPTY = {
  messages: [] as ChatMessage[],
  isStreaming: false,
  statusText: '',
  conversationId: null as string | null,
}

export const useAgentStore = create<AgentState>((set) => ({
  ...EMPTY,

  addMessage: (message: ChatMessage) =>
    set((state) => ({ messages: [...state.messages, message] })),

  patchStreaming: (delta: string) =>
    set((state) => {
      const last = state.messages[state.messages.length - 1]
      if (!last || last.role !== 'agent' || last.status !== 'streaming') {
        return state
      }
      const patched: ChatMessage = { ...last, text: last.text + delta }
      return {
        messages: [...state.messages.slice(0, -1), patched],
      }
    }),

  replaceStreamingText: (text: string) =>
    set((state) => {
      const last = state.messages[state.messages.length - 1]
      if (!last || last.role !== 'agent' || last.status !== 'streaming') {
        return state
      }
      const patched: ChatMessage = { ...last, text }
      return {
        messages: [...state.messages.slice(0, -1), patched],
      }
    }),

  finalize: (status: ChatMessage['status']) =>
    set((state) => {
      const last = state.messages[state.messages.length - 1]
      if (!last || last.role !== 'agent' || last.status !== 'streaming') {
        return state
      }
      const finalised: ChatMessage = { ...last, status }
      return {
        messages: [...state.messages.slice(0, -1), finalised],
      }
    }),

  setStatus: (statusText: AgentStatus) => set({ statusText }),

  setStreaming: (isStreaming: boolean) => set({ isStreaming }),

  setConversationId: (conversationId: string | null) =>
    set({ conversationId }),

  reset: () => set({ ...EMPTY }),
}))
